import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, UsageOverview, UsageContent } from '../api';
import { Layout, Loading, ErrorBox } from '../components/Layout';
import { STATUS_STYLE, expiryStyle, expiryLabel, relativeDay, WINDOWS } from './usage-shared';

/**
 * Phase 7 Part B — the super admin's usage area.
 *
 * Read-only throughout: nothing here mutates, and every number is aggregated
 * server-side from data already recorded (DownloadLog written at mint time,
 * User.lastLoginAt, School.*). Content activity is roster-level by
 * construction — DownloadLog stores the school, never the user — so this
 * answers "which schools need attention" without becoming per-teacher
 * surveillance.
 */
export function AdminUsage() {
  const [days, setDays] = useState<number | undefined>(30);
  const [data, setData] = useState<UsageOverview | null>(null);
  const [content, setContent] = useState<UsageContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setContent(null);
    setError(null);

    void Promise.all([api.admin.usage.overview(days), api.admin.usage.content(days, 15)])
      .then(([o, c]) => {
        // The window can change while a fetch is in flight; a late response for
        // the previous window would otherwise overwrite the current one.
        if (!live) return;
        setData(o);
        setContent(c);
      })
      .catch((e: Error) => live && setError(e.message));

    return () => {
      live = false;
    };
  }, [days]);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Usage</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            How each school is using its licence.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">
            Activity window{' '}
            <select
              value={days ?? 'all'}
              onChange={(e) =>
                setDays(e.target.value === 'all' ? undefined : Number(e.target.value))
              }
              className="ml-1 border border-slate-300 rounded-md px-2 py-1 text-sm bg-white"
            >
              {WINDOWS.map((w) => (
                <option key={w.label} value={w.days ?? 'all'}>
                  {w.label}
                </option>
              ))}
            </select>
          </label>
          <Link
            to="/admin/schools"
            className="text-sm px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Manage schools
          </Link>
        </div>
      </div>

      {error && <ErrorBox message={error} />}
      {!data && !error && <Loading />}

      {data && (
        <>
          <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <Stat label="Schools" value={data.totals.schools} sub={`${data.totals.activeSchools} active`} />
            <Stat
              label="Seats used"
              value={data.totals.seatsUsed}
              sub={data.totals.seatsLicensed ? `of ${data.totals.seatsLicensed} sold` : 'uncapped'}
            />
            <Stat
              label="Expiring ≤30d"
              value={data.totals.expiringWithin30Days}
              tone={data.totals.expiringWithin30Days > 0 ? 'amber' : undefined}
            />
            <Stat
              label="Expired"
              value={data.totals.expiredLicences}
              tone={data.totals.expiredLicences > 0 ? 'red' : undefined}
            />
            <Stat
              label="Users"
              value={data.totals.admins + data.totals.coordinators + data.totals.teachers}
              sub={`${data.totals.coordinators} coord · ${data.totals.teachers} teach`}
            />
            <Stat
              label="Lessons opened"
              value={data.totals.views + data.totals.downloads}
              sub={`${data.totals.downloads} downloaded`}
            />
          </section>

          <h2 className="text-sm font-semibold text-slate-700 mb-2">Per school</h2>
          <div className="overflow-x-auto border border-slate-200 rounded-md bg-white mb-8">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-3 py-2">School</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="text-right font-medium px-3 py-2">Seats</th>
                  <th className="text-right font-medium px-3 py-2">Coord</th>
                  <th className="text-right font-medium px-3 py-2">Teach</th>
                  <th className="text-left font-medium px-3 py-2">Licence ends</th>
                  <th className="text-left font-medium px-3 py-2">Last activity</th>
                  <th className="text-right font-medium px-3 py-2">Opened</th>
                </tr>
              </thead>
              <tbody>
                {data.schools.map((s) => {
                  const overSeats = s.seatLimit != null && s.seatsUsed > s.seatLimit;
                  return (
                    <tr key={s.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <Link
                          to={`/admin/usage/${s.id}`}
                          className="text-slate-900 font-medium hover:underline"
                        >
                          {s.name}
                        </Link>
                        {s.entitlementCount === 0 && (
                          <span className="ml-2 text-xs text-amber-700">
                            no entitlements — sees nothing
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            'inline-block px-2 py-0.5 rounded border text-xs ' +
                            (STATUS_STYLE[s.status] ?? '')
                          }
                        >
                          {s.status}
                        </span>
                      </td>
                      <td
                        className={
                          'px-3 py-2 text-right tabular-nums ' +
                          (overSeats ? 'text-red-700 font-medium' : 'text-slate-700')
                        }
                      >
                        {s.seatsUsed}
                        {s.seatLimit != null ? ` / ${s.seatLimit}` : ' / ∞'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {s.coordinators}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {s.teachers}
                      </td>
                      <td className={'px-3 py-2 ' + expiryStyle(s.daysToExpiry)}>
                        {expiryLabel(s.licenseEnd, s.daysToExpiry)}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {relativeDay(s.lastActivityAt)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {s.views + s.downloads}
                        {s.downloads > 0 && (
                          <span className="text-slate-400"> ({s.downloads}↓)</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.schools.length === 0 && (
              <p className="text-slate-500 px-3 py-4">No schools yet.</p>
            )}
          </div>

          <h2 className="text-sm font-semibold text-slate-700 mb-2">Most-opened content</h2>
          {content && content.items.length === 0 && (
            <p className="text-slate-500 border border-slate-200 rounded-md bg-white px-3 py-4">
              No recorded activity in this window.
            </p>
          )}
          {content && content.items.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-md bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Lesson</th>
                    <th className="text-left font-medium px-3 py-2">Grade / subject</th>
                    <th className="text-left font-medium px-3 py-2">Variant</th>
                    <th className="text-right font-medium px-3 py-2">Views</th>
                    <th className="text-right font-medium px-3 py-2">Downloads</th>
                    <th className="text-left font-medium px-3 py-2">Schools</th>
                  </tr>
                </thead>
                <tbody>
                  {content.items.map((i) => (
                    <tr key={i.videoFileId} className="border-t border-slate-100 align-top">
                      <td className="px-3 py-2">
                        <span className="text-slate-900">{i.videoTitle}</span>
                        {i.bookTitle && (
                          <span className="block text-xs text-slate-500">{i.bookTitle}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {[i.gradeName, i.subjectName].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs">
                        {[i.language, i.quality].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {i.views}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {i.downloads}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">
                        {/* Named rather than just counted: "which schools use
                            this" is the question that drives renewals. */}
                        {i.schools.slice(0, 3).map((s) => (
                          <span key={s.schoolId} className="block">
                            {s.schoolName} ({s.views + s.downloads})
                          </span>
                        ))}
                        {i.schoolCount > 3 && (
                          <span className="text-slate-400">
                            +{i.schoolCount - 3} more
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: number;
  sub?: string;
  tone?: 'amber' | 'red';
}) {
  const toneClass =
    tone === 'red' ? 'text-red-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="border border-slate-200 rounded-md bg-white px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={'text-lg font-semibold tabular-nums ' + toneClass}>{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );
}
